import React from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import dayjs from 'dayjs';
import { CheckCircle2, Clock } from 'lucide-react';

export const PageHeader = ({ title, description, actions, children, className, wash = true, testId = 'page-header' }) => (
  <div
    data-testid={testId}
    className={cn(
      'sticky top-0 z-20 border-b',
      'bg-[hsl(var(--background))]/85 backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--background))]/70',
      wash && 'calm-header-wash',
      className
    )}
  >
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 lg:px-8 py-4">
      <div className="min-w-0">
        <h1 className="font-display text-xl md:text-2xl font-semibold tracking-tight text-foreground truncate" data-testid={`${testId}-title`}>
          {title}
        </h1>
        {description && (
          <p className="text-sm text-muted-foreground mt-0.5" data-testid={`${testId}-desc`}>{description}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
    {children && <div className="px-4 sm:px-6 lg:px-8 pb-3">{children}</div>}
  </div>
);

export const StatCard = ({ label, value, hint, icon: Icon, tone = 'default', testId = 'stat-card' }) => {
  const toneCls = {
    default: 'bg-card',
    good: 'bg-[hsl(148_68%_95%)]',
    warn: 'bg-[hsl(33_96%_95%)]',
    danger: 'bg-[hsl(4_90%_97%)]',
    primary: 'bg-[hsl(174_60%_95%)]',
  }[tone];
  return (
    <div data-testid={testId} className={cn('rounded-xl border p-4 sm:p-5 shadow-[0_1px_0_rgba(16,24,40,0.04)]', toneCls)}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <p className="font-display text-2xl md:text-3xl font-semibold tabular-nums mt-2 text-foreground">{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
};

export const PriorityBadge = ({ priority = 'p2' }) => {
  const map = {
    p0: { label: 'P0 High', cls: 'bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] border-transparent' },
    p1: { label: 'P1 Medium', cls: 'bg-[hsl(var(--warn-soft))] text-[hsl(var(--warn))] border-transparent' },
    p2: { label: 'P2 Low', cls: 'bg-[hsl(174_50%_92%)] text-[hsl(174_78%_26%)] border-transparent' },
  };
  const p = map[priority] || map.p2;
  return <Badge className={cn('font-medium', p.cls)} data-testid="priority-badge">{p.label}</Badge>;
};

export const StageBadge = ({ stage = 'sourced' }) => {
  const map = {
    sourced:   'bg-[hsl(200_40%_94%)] text-[hsl(200_50%_28%)]',
    screening: 'bg-[hsl(43_74%_94%)] text-[hsl(30_90%_30%)]',
    interview: 'bg-[hsl(28_70%_94%)] text-[hsl(28_70%_30%)]',
    offer:     'bg-[hsl(174_50%_92%)] text-[hsl(174_78%_24%)]',
    hired:     'bg-[hsl(148_68%_92%)] text-[hsl(148_82%_22%)]',
    rejected:  'bg-[hsl(4_90%_95%)] text-[hsl(4_74%_36%)]',
  };
  const cls = map[stage] || map.sourced;
  return <Badge className={cn('font-medium capitalize border-transparent', cls)} data-testid="stage-badge">{stage}</Badge>;
};

export const TimelineItem = ({ event }) => (
  <div className="grid grid-cols-[16px_1fr] gap-3" data-testid="timeline-item">
    <div className="relative pt-1">
      <div className="h-3 w-3 rounded-full bg-primary ring-4 ring-primary/10" />
    </div>
    <div className="pb-4">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-foreground">{event.title}</p>
        <span className="text-xs text-muted-foreground shrink-0">{dayjs(event.timestamp).format('DD MMM, hh:mm A')}</span>
      </div>
      {event.detail && <p className="text-sm text-muted-foreground mt-0.5 whitespace-pre-wrap">{event.detail}</p>}
      {event.actor_name && <p className="text-xs text-muted-foreground mt-1">by {event.actor_name}</p>}
    </div>
  </div>
);

export const EmptyState = ({ title, description, action, icon: Icon, testId = 'empty-state' }) => (
  <div data-testid={testId} className="rounded-xl border bg-card p-10 text-center flex flex-col items-center gap-3">
    {Icon && <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Icon className="h-6 w-6" /></div>}
    <h3 className="font-display text-lg font-semibold">{title}</h3>
    {description && <p className="text-sm text-muted-foreground max-w-md">{description}</p>}
    {action}
  </div>
);

export const TaskCard = ({ task, onClick }) => {
  const overdue = task.overdue;
  return (
    <button
      onClick={onClick}
      data-testid="task-card"
      className={cn(
        'w-full text-left rounded-xl border bg-card p-4 hover:bg-accent transition-colors',
        overdue && 'border-[hsl(var(--danger))]/40'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-foreground truncate">{task.title}</p>
          <p className="text-sm text-muted-foreground truncate">{task.subtitle}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <StageBadge stage={task.stage} />
            <PriorityBadge priority={task.priority} />
            <span className="text-xs text-muted-foreground">· {task.company_name}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className={cn(
            'inline-flex items-center gap-1 text-xs font-medium',
            overdue ? 'text-[hsl(var(--danger))]' : 'text-muted-foreground'
          )}>
            {overdue ? <Clock className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
            {task.next_followup_date ? dayjs(task.next_followup_date).format('DD MMM') : 'No date'}
          </div>
          <p className="text-xs mt-1 text-muted-foreground capitalize">next: {task.next_action || 'call'}</p>
        </div>
      </div>
    </button>
  );
};
